import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, TestTube, User, Calendar, Building2, FileText, Stethoscope } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ValidateExamRequest() {
  const { requestId } = useParams();
  const [loading, setLoading] = useState(true);
  const [validationResult, setValidationResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const validateExamRequest = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/validate-exam/${requestId}`);
        setValidationResult(response.data);
      } catch (err) {
        console.error('Erro na validação:', err);
        setError('Não foi possível validar esta solicitação de exame.');
      } finally {
        setLoading(false);
      }
    };

    if (requestId) {
      validateExamRequest();
    }
  }, [requestId]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Não informada';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-teal-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="animate-spin w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Validando solicitação de exame...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardHeader className="text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-2" />
            <CardTitle className="text-red-700">Erro na Validação</CardTitle>
            <CardDescription className="text-red-600">{error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (!validationResult?.valid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardHeader className="text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-2" />
            <CardTitle className="text-red-700">Solicitação Não Encontrada</CardTitle>
            <CardDescription className="text-red-600">
              Esta solicitação de exame não foi encontrada em nosso sistema.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-gray-500">
              Verifique se o código QR está correto ou entre em contato com a unidade de saúde.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-teal-50 to-blue-50 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header de Sucesso */}
        <Card className="mb-6 border-teal-200 bg-gradient-to-r from-teal-50 to-cyan-50">
          <CardHeader className="text-center pb-2">
            <CheckCircle className="w-16 h-16 text-teal-500 mx-auto mb-2" />
            <CardTitle className="text-teal-700 text-2xl">Solicitação de Exame Válida</CardTitle>
            <CardDescription className="text-teal-600">
              Esta solicitação foi verificada com sucesso
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Badge variant="outline" className="text-lg px-4 py-2 bg-white border-teal-300 text-teal-700 font-mono">
              Código: {validationResult.validation_code}
            </Badge>
          </CardContent>
        </Card>

        {/* Dados do Paciente */}
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-blue-500" />
              Dados do Paciente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-500">Nome:</span>
              <span className="font-semibold text-gray-800">{validationResult.patient_name}</span>
            </div>
            {validationResult.patient_birth_date && (
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-gray-500">Data de Nascimento:</span>
                <span className="font-medium">{validationResult.patient_birth_date}</span>
              </div>
            )}
            {validationResult.patient_sex && (
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-gray-500">Sexo:</span>
                <span className="font-medium">
                  {validationResult.patient_sex === 'M' ? 'Masculino' : 'Feminino'}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Exames Solicitados */}
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TestTube className="w-5 h-5 text-cyan-500" />
              Exames Solicitados
              <Badge className="ml-auto bg-cyan-100 text-cyan-700">
                {validationResult.total_exams} exame(s)
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {validationResult.exams?.map((exam, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-2 py-2 px-3 bg-gray-50 rounded-lg"
                >
                  <span className="w-6 h-6 flex items-center justify-center bg-cyan-100 text-cyan-700 rounded-full text-sm font-medium">
                    {index + 1}
                  </span>
                  <span className="text-gray-700">{exam}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Observações */}
        {validationResult.observations && (
          <Card className="mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-500" />
                Observações Clínicas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 bg-amber-50 p-3 rounded-lg">
                {validationResult.observations}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Informações da Solicitação */}
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-500" />
              Informações da Solicitação
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-500">Unidade de Saúde:</span>
              <span className="font-medium">{validationResult.unit_name}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-500">Solicitado por:</span>
              <span className="font-medium flex items-center gap-2">
                <Stethoscope className="w-4 h-4 text-gray-400" />
                {validationResult.created_by}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-500">Data da Solicitação:</span>
              <span className="font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                {formatDate(validationResult.created_at)}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-500">Status:</span>
              <Badge className="bg-green-100 text-green-700">
                {validationResult.status === 'emitida' ? 'Emitida' : validationResult.status}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 mt-6">
          <p>Sistema de Receitas Médicas - Fundo Municipal de Saúde</p>
          <p>São Domingos do Prata - MG</p>
        </div>
      </div>
    </div>
  );
}
